import { type ReactNode, useEffect, useRef, useState } from 'react'
import styled from 'styled-components'

export type ToggleCollapseFn = () => void

type CollapsibleProps = {
  header:
    | ((toggleCollapse: ToggleCollapseFn, collapsed: boolean) => void)
    | React.ReactNode
  children?: ReactNode
  defaultCollapsed?: boolean
  className?: string
  onCollapseChange?: (isCollapsed: boolean) => void
}

const TRANSITION_DURATION = 0.5

const CollapsibleItem = ({
  header,
  children,
  defaultCollapsed = true,
  className,
  onCollapseChange
}: CollapsibleProps) => {
  const [collapsed, setCollapsed] = useState(defaultCollapsed)
  const contentRef = useRef<HTMLDivElement>(null)

  const toggleCollapse: ToggleCollapseFn = () => {
    setCollapsed((prevCollapsed) => !prevCollapsed)
  }

  // biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
  useEffect(() => {
    onCollapseChange?.(collapsed)
  }, [collapsed])

  // biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
  useEffect(() => {
    if (contentRef.current) {
      const contentHeightRef = contentRef.current.scrollHeight
      contentRef.current.style.height = collapsed
        ? '0'
        : `${contentHeightRef}px`
    }
  }, [collapsed, children])

  return (
    <CollapsibleWrapper className={className} $isCollapsed={collapsed}>
      <>
        {typeof header === 'function' && header(toggleCollapse, collapsed)}
        <CollapsibleContentWrapper ref={contentRef}>
          <CollapsibleContent>{children}</CollapsibleContent>
        </CollapsibleContentWrapper>
      </>
    </CollapsibleWrapper>
  )
}

const CollapsibleWrapper = styled.div<{
  $isCollapsed: boolean
}>`
  overflow: hidden;

  transition: background-color ${TRANSITION_DURATION}s;
`

const CollapsibleContentWrapper = styled.div`
  transition: height ${TRANSITION_DURATION}s;
`
const CollapsibleContent = styled.div``
export default CollapsibleItem
